import React from 'react';
import { Link } from 'react-router-dom';
import PageSeo from './components/PageSeo';
import { useDarkMode } from './DarkModeContext';
import Footer from './Footer';

function TermsOfService() {
    const { darkMode } = useDarkMode();
    
    return (
        <>
        <PageSeo
            title="Terms of Service – Merra AI"
            description="Read Merra AI terms of service. Terms and conditions for using our AI-powered video creation platform."
            path="/terms-of-service"
            keywords="terms of service, terms and conditions, Merra terms, user agreement"
        />
        <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <h1 style={{ color: darkMode ? '#ffffff' : '#2c3e50', borderBottom: `2px solid ${darkMode ? '#4A9EFF' : '#3498db'}`, paddingBottom: '10px' }}>
                Merra – Terms of Service
            </h1>
            
            <p style={{ color: darkMode ? '#b0b0b0' : '#666', fontStyle: 'italic' }}>
                Last updated: March 4, 2026
            </p>

            <p>
                Welcome to Merra. By accessing or using Merra ("the Service," "we," "our"), you agree to be bound by these Terms of Service. Please read them carefully.
            </p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>1. Service Description</h2>
            <p>
                Merra provides an AI-powered platform that generates and edits videos from user-uploaded content. Features may evolve, and we may update or discontinue parts of the Service at any time.
            </p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>2. Eligibility</h2>
            <ul>
                <li>You must be at least 16 years old to use Merra.</li>
                <li>By using the Service, you represent that you have the authority to enter into this agreement.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>3. User Content & Responsibilities</h2>
            <ul>
                <li><strong>Ownership</strong>: You retain ownership of all content you upload (videos, images, audio, text).</li>
                <li><strong>License</strong>: You grant Merra a limited license to process, transform, and store your content solely for the purpose of providing the Service.</li>
                <li><strong>Compliance</strong>: You are responsible for ensuring your content complies with applicable laws, third-party rights (e.g., copyright, likeness rights), and the policies of the platforms where you publish (Instagram, TikTok, YouTube, etc.).</li>
                <li><strong>Voice recordings</strong>: You confirm that you have the necessary rights and permissions to upload and synthesize any voice recordings submitted to the Service. You must not upload voices of individuals without their authorization.</li>
                <li><strong>Prohibited Use</strong>: You may not use Merra to generate or distribute content that is illegal, defamatory, hateful, harassing, pornographic, or otherwise inappropriate.</li>
            </ul>
            <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginTop: '20px' }}>AI Generated Content</h3>
            <p style={{ color: darkMode ? '#e0e0e0' : '#333', marginTop: '8px' }}>
                Merra uses artificial intelligence to assist users in generating scripts, voiceovers, and edited videos from user-submitted content. Users remain responsible for the content they upload and the outputs they create using the Service.
            </p>
            <p style={{ color: darkMode ? '#e0e0e0' : '#333' }}>
                Users may not use Merra to create or distribute content that is unlawful, abusive, defamatory, harassing, hateful, sexually explicit involving minors, or that impersonates individuals or organizations without authorization.
            </p>
            <p style={{ color: darkMode ? '#e0e0e0' : '#333' }}>
                Merra reserves the right to restrict access to the Service or remove content if it is used in violation of these guidelines.
            </p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>4. Intellectual Property</h2>
            <ul>
                <li>Merra owns all rights to the platform, software, algorithms, and models that power the Service.</li>
                <li>You may not copy, reverse-engineer, or redistribute the Service without our consent.</li>
                <li>Generated outputs: You receive a broad right to use the videos generated from your uploaded content.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>5. No Guarantee of Compliance or Results</h2>
            <ul>
                <li><strong>Platform Compliance</strong>: Merra does not guarantee that generated videos will comply with the policies of Instagram, TikTok, or other third-party platforms. If a video is taken down, flagged, or restricted, Merra is not liable.</li>
                <li><strong>Performance</strong>: We do not guarantee improved engagement, reach, or business results. The Service is a tool, not a promise of outcomes.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>6. Payments, Subscriptions & Refunds</h2>
            <ul>
                <li>Paid plans are billed in advance (monthly or yearly, as selected).</li>
                <li>Subscriptions automatically renew unless canceled before the renewal date.</li>
                <li>Fees are non-refundable except where required by law.</li>
                <li>We may change pricing with prior notice; continued use of the Service after new pricing takes effect constitutes acceptance.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>7. Free & Trial Accounts</h2>
            <ul>
                <li>Free or trial accounts may have limited functionality.</li>
                <li>We may terminate or limit free access at any time without notice.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>8. Data & Privacy</h2>
            <ul>
                <li>Our collection and use of personal data is governed by our <strong><Link to="/privacy-policy" style={{ color: darkMode ? '#4A9EFF' : '#1976d2' }}>Privacy & Data Policy</Link></strong>.</li>
                <li>By using Merra, you consent to such collection and use.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>9. Service Availability</h2>
            <ul>
                <li>We aim for high uptime but do not guarantee uninterrupted access.</li>
                <li>The Service may experience downtime due to maintenance, updates, or issues beyond our control (e.g., third-party provider outages).</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>10. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul>
                <li>Merra is provided "as is" and "as available."</li>
                <li>We are not liable for indirect, incidental, or consequential damages (including lost profits, lost opportunities, data loss).</li>
                <li>Our total liability for any claim will not exceed the amount you paid in the 3 months before the claim arose.</li>
                <li>We are not responsible for takedowns, bans, or restrictions imposed by third-party platforms.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>11. Indemnification</h2>
            <p>You agree to indemnify and hold harmless Merra, its team, and affiliates from any claims, damages, or expenses arising out of:</p>
            <ul>
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your uploaded content or generated outputs</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>12. Termination</h2>
            <ul>
                <li>You may cancel your account at any time.</li>
                <li>We may suspend or terminate your account if you violate these Terms or misuse the Service.</li>
                <li>Upon termination, your right to use the Service ceases immediately.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>13. Governing Law & Dispute Resolution</h2>
            <ul>
                <li>These Terms are governed by the laws of Portugal.</li>
                <li>Any disputes will be resolved in the courts of Portugal.</li>
            </ul>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>14. Changes to Terms</h2>
            <p>We may update these Terms from time to time. Significant changes will be communicated via email or in-app notifications. Continued use after changes take effect constitutes acceptance.</p>

            <h2 style={{ color: darkMode ? '#ffffff' : '#2c3e50', marginTop: '30px' }}>15. Contact</h2>
            <p>For questions about these Terms, contact us at:</p>
            <p>📧 <strong><a href="mailto:hello@merra.ai" style={{ color: darkMode ? '#4A9EFF' : '#1976d2' }}>hello@merra.ai</a></strong></p>
            <Footer />
        </div>
        </>
    );
}

export default TermsOfService;
